/**
 * The brand mark — "Interference".
 *
 * Two crescents of one ring, 180 degrees apart, separated by a constant-width
 * channel: two wavefronts arriving from opposite walls, with the line where
 * they cancel cut out between them.
 *
 * ── Why this is geometry and not a checked-in path string ────────────────────
 *
 * The mark was designed as two thick round-capped arcs with the channel painted
 * over them by a background-coloured "halo" stroke. That renders correctly on a
 * known ground and is wrong as a shipped asset: the halo paints an opaque
 * colour, so the mark grows a visible band the moment it sits on anything other
 * than the ground it was drawn against.
 *
 * So the strokes are converted to closed filled outlines here, and the channel
 * becomes real transparency. That conversion normally wants a boolean path
 * library, but both crescents are arcs of the SAME circle, which collapses it to
 * plain trigonometry:
 *
 *   • the FRONT crescent is an ordinary round-capped band;
 *   • the BACK crescent is the same band with each end cut by a circle of
 *     radius `w / 2 + gap` centred on a front cap centre.
 *
 * That cutting radius is the whole trick — it is the front cap radius plus the
 * channel width, so the gap stays exactly `gap` wide the whole way round rather
 * than pinching or flaring where the two cross.
 *
 * Keeping it as functions rather than baked strings means the channel width and
 * stroke weight stay legible and adjustable, and `__tests__/logo.test.ts` can
 * assert the constant-gap property directly instead of trusting a blob of
 * numbers. `INTERFERENCE_MARK` below is the resolved, shipped mark; everything
 * that draws the logo should read that rather than rebuilding it.
 *
 * Colours are NOT here. The mark is two shapes and the caller supplies two fills
 * from `palette.ts` — see `web/src/components/brand/Logo.tsx` for the contexts.
 */

/** Everything is authored in one square; every consumer scales from this. */
export const LOGO_VIEWBOX = '0 0 100 100';
export const LOGO_SIZE = 100;

export interface InterferenceGeometry {
  /** Ring centre. */
  cx: number;
  cy: number;
  /** Radius of the ring's centreline. */
  r: number;
  /** Band thickness — the old stroke-width. */
  w: number;
  /** Width of the transparent channel between the two crescents. */
  gap: number;
  /** Where the back crescent starts, in degrees (0 = east, 90 = south). */
  start: number;
  /** Angular length of each crescent. Over 180 so the two ends interleave. */
  span: number;
}

/**
 * The shipped mark. `span` deliberately exceeds 180 degrees: that overlap is
 * what makes one crescent tuck past the other instead of the two meeting in a
 * plain diameter, and it is the difference between a logo and a pie chart.
 */
export const INTERFERENCE: InterferenceGeometry = {
  cx: 50,
  cy: 50,
  r: 27,
  w: 24,
  gap: 4.08,
  start: 135,
  span: 190,
};

const RAD = Math.PI / 180;
const round = (n: number): number => Number(n.toFixed(3));

type Point = readonly [number, number];

const polar = (cx: number, cy: number, r: number, deg: number): Point => [
  cx + r * Math.cos(deg * RAD),
  cy + r * Math.sin(deg * RAD),
];

/** Both intersection points of two circles, or null when they do not meet. */
function circleIntersect(c1: Point, r1: number, c2: Point, r2: number): [Point, Point] | null {
  const dx = c2[0] - c1[0];
  const dy = c2[1] - c1[1];
  const d = Math.hypot(dx, dy);
  if (d === 0 || d > r1 + r2 || d < Math.abs(r1 - r2)) return null;
  const a = (d * d + r1 * r1 - r2 * r2) / (2 * d);
  const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
  const bx = c1[0] + (a * dx) / d;
  const by = c1[1] + (a * dy) / d;
  return [
    [bx + (h * dy) / d, by - (h * dx) / d],
    [bx - (h * dy) / d, by + (h * dx) / d],
  ];
}

const angleOf = (cx: number, cy: number, p: Point): number => {
  const a = Math.atan2(p[1] - cy, p[0] - cx) / RAD;
  return a < 0 ? a + 360 : a;
};

/** Signed difference b - a, wrapped into (-180, 180]. */
const angDiff = (a: number, b: number): number => {
  const d = (((b - a + 540) % 360) - 180);
  return d === -180 ? 180 : d;
};

const norm360 = (deg: number): number => ((deg % 360) + 360) % 360;

export interface InterferencePaths {
  /** The crescent that is cut where the other crosses it. */
  back: string;
  /** The uncut, round-capped crescent that reads as being in front. */
  front: string;
  /** Cap centres of the front crescent — the two circles that cut `back`. */
  capCentres: readonly [Point, Point];
  /** Radius those cutting circles use. */
  cutRadius: number;
  /** Outer and inner radii of both bands. */
  outerRadius: number;
  innerRadius: number;
}

export function buildInterference(geo: InterferenceGeometry = INTERFERENCE): InterferencePaths {
  const { cx, cy, r, w, gap, start, span } = geo;
  const ro = r + w / 2;
  const ri = r - w / 2;
  const cap = w / 2;
  const cut = w / 2 + gap;

  // The front crescent sits opposite the back one.
  const fStart = start + 180;
  const fEnd = start + 180 + span;

  const at = (rad: number, deg: number): string => polar(cx, cy, rad, deg).map(round).join(' ');
  const sweepArc = (rad: number, from: number, to: number, sweep: 0 | 1): string =>
    `A ${round(rad)} ${round(rad)} 0 ${Math.abs(to - from) > 180 ? 1 : 0} ${sweep} ${at(rad, to)}`;

  const front =
    `M ${at(ro, fStart)} ` +
    `${sweepArc(ro, fStart, fEnd, 1)} ` +
    `A ${round(cap)} ${round(cap)} 0 0 1 ${at(ri, fEnd)} ` +
    `${sweepArc(ri, fEnd, fStart, 0)} ` +
    `A ${round(cap)} ${round(cap)} 0 0 1 ${at(ro, fStart)} Z`;

  const capStart = polar(cx, cy, r, fStart);
  const capEnd = polar(cx, cy, r, fEnd);

  /**
   * Both intersections of a cutting circle sit the SAME angular distance from
   * its cap centre, so they can only be told apart by DIRECTION. The back
   * crescent leaves `capEnd` on the increasing-angle side and arrives at
   * `capStart` from the decreasing-angle side. Choosing by distance instead
   * picks arbitrarily and silently produces a different shape.
   */
  const pickSide = (pts: [Point, Point] | null, refDeg: number, wantPositive: boolean): Point => {
    if (!pts) throw new Error('logo: cutting circle does not meet the band — check gap/w/r');
    return angDiff(refDeg, angleOf(cx, cy, pts[0])) > 0 === wantPositive ? pts[0] : pts[1];
  };

  const aEnd = norm360(fEnd);
  const aStart = norm360(fStart);
  const O: Point = [cx, cy];

  const outerFromEnd = pickSide(circleIntersect(O, ro, capEnd, cut), aEnd, true);
  const innerFromEnd = pickSide(circleIntersect(O, ri, capEnd, cut), aEnd, true);
  const outerToStart = pickSide(circleIntersect(O, ro, capStart, cut), aStart, false);
  const innerToStart = pickSide(circleIntersect(O, ri, capStart, cut), aStart, false);

  const arcSpan = (from: number, to: number): number => norm360(to - from);
  const largeOuter = arcSpan(angleOf(cx, cy, outerFromEnd), angleOf(cx, cy, outerToStart)) > 180 ? 1 : 0;
  const largeInner = arcSpan(angleOf(cx, cy, innerFromEnd), angleOf(cx, cy, innerToStart)) > 180 ? 1 : 0;
  const pt = (p: Point): string => p.map(round).join(' ');

  // Sweep 0 on the cut arcs is what curves them INTO the crescent. Sweep 1
  // bulges them the other way and flattens both ends into straight nicks.
  const back =
    `M ${pt(outerFromEnd)} ` +
    `A ${round(ro)} ${round(ro)} 0 ${largeOuter} 1 ${pt(outerToStart)} ` +
    `A ${round(cut)} ${round(cut)} 0 0 0 ${pt(innerToStart)} ` +
    `A ${round(ri)} ${round(ri)} 0 ${largeInner} 0 ${pt(innerFromEnd)} ` +
    `A ${round(cut)} ${round(cut)} 0 0 0 ${pt(outerFromEnd)} Z`;

  return {
    back,
    front,
    capCentres: [capStart, capEnd],
    cutRadius: cut,
    outerRadius: ro,
    innerRadius: ri,
  };
}

/** The resolved mark. Draw this; do not rebuild it per render. */
export const INTERFERENCE_MARK: InterferencePaths = buildInterference();

/**
 * Standalone SVG, for the files that are handed to things which cannot run
 * React — favicons, printers, email, a designer's asset folder.
 *
 * This lives beside the geometry rather than in the emit script so the string
 * form and the React component cannot describe two different logos.
 */
export type LogoSvgVariant =
  /** Two-tone, transparent ground. The general-purpose asset. */
  | 'mark'
  /** One colour via `currentColor`, transparent ground. */
  | 'mark-mono'
  /** Two-tone tuned for a dark ground, still transparent. */
  | 'mark-inverse'
  /** The app tile: dark squircle with the inverse mark on it. */
  | 'tile';

export function renderLogoSvg(
  variant: LogoSvgVariant,
  colors: { ink: string; accent: string; label: string; accentOnDark: string; brandTile: string },
  size: number = 512,
): string {
  const { back, front } = INTERFERENCE_MARK;
  const fills =
    variant === 'mark'
      ? { back: colors.ink, front: colors.accent, tile: null }
      : variant === 'mark-mono'
        ? { back: 'currentColor', front: 'currentColor', tile: null }
        : variant === 'mark-inverse'
          ? { back: colors.label, front: colors.accentOnDark, tile: null }
          : { back: colors.label, front: colors.accentOnDark, tile: colors.brandTile };

  const tile = fills.tile ? `\n  <path d="${logoTilePath()}" fill="${fills.tile}"/>` : '';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" ` +
    `viewBox="${LOGO_VIEWBOX}" role="img" aria-label="roomtreatment.diy">` +
    tile +
    `\n  <path d="${back}" fill="${fills.back}"/>` +
    `\n  <path d="${front}" fill="${fills.front}"/>\n</svg>\n`
  );
}

/**
 * The app-tile shape. A true superellipse would need a curve fit; this is the
 * quadratic approximation every platform's icon grid is already close to, and
 * at icon sizes the difference is under a pixel.
 */
export function logoTilePath(size: number = LOGO_SIZE, radiusRatio = 0.235): string {
  const r = round(size * radiusRatio);
  const s = round(size);
  return (
    `M ${r} 0 L ${round(size - size * radiusRatio)} 0 Q ${s} 0 ${s} ${r} ` +
    `L ${s} ${round(size - size * radiusRatio)} Q ${s} ${s} ${round(size - size * radiusRatio)} ${s} ` +
    `L ${r} ${s} Q 0 ${s} 0 ${round(size - size * radiusRatio)} ` +
    `L 0 ${r} Q 0 0 ${r} 0 Z`
  );
}
